import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSharedStore } from '../../store/sharedStore';
import { userService } from '../../services/userService';
import { sharedService, type UploadedFile } from '../../services/sharedService';
import type { CreateUserRequest } from '../../types/api';
import {
  InfoCard,
  PageHeader,
  Button,
  Input,
  PhoneInput,
  Select,
  Spinner,
  PhotoCropModal,
} from '../../components';
import type { SelectOption } from '../../components';
import { PlusIcon, TrashIcon } from '@radix-ui/react-icons';
import { ASSETS } from '../../constants/assets';
import { usePhotoCropFlow } from '../../hooks/usePhotoCropFlow';
import { useGalleryPhotoCropUpload } from '../../hooks/useGalleryPhotoCropUpload';
import { useEmailValidation } from '../../hooks/useEmailValidation';
import { useProfileFormValidation } from '../../hooks/useProfileFormValidation';
import { formatApiError, getFieldErrorsFromApi } from '../../utils/errorHandler';
import {
  genderOptions,
  manglikOptions,
  horoscopeOptions,
  DEFAULT_PHONE_COUNTRY_CODE,
  buildFullPhoneNumber,
  parsePhoneNumber,
  buildProfilePhotoPayload,
} from './profileFormConstants';
import {
  PROFILE_FIELD_LABELS,
  getMaxDateOfBirthForMinAge,
  type ProfileValidationField,
} from './profileFormValidation';
import './CreateUser.scss';

const CREATE_USER_SECTION_ICONS = ASSETS.icons.createUser;

// ============ Types ============

interface ProfileFormData {
  // Personal Info
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  phoneCountryCode: string;
  phoneNumber: string;
  phoneE164: string;
  gender: string;
  dateOfBirth: string;
  // Profile Details
  motherTongueId: string;
  religionId: string;
  maritalStatusId: string;
  heightId: string;
  // New Physical Fields
  bodyTypeId: string;
  weight: string;
  complexionId: string;
  bloodGroupId: string;
  disabilityId: string;
  // Dynamic Religion Fields
  sectId: string;
  casteId: string;
  jamatId: string;
  casteNoBar: boolean;
  // Profile fields aligned with edit form
  manglikId: string;
  horoscopeNecessity: string;
  // Career Details
  countryId: string;
  stateId: string;
  cityId: string;
  degreeId: string;
  employedInId: string;
  occupationId: string;
  income_currency_id: string;
  annualIncome: string;
  aboutYou: string;
  // Lifestyle & Family
  drinkingHabitId: string;
  smokingHabitId: string;
  dietId: string;
  familyTypeId: string;
  fatherOccupationId: string;
  motherOccupationId: string;
  brothers: string;
  marriedBrothers: string;
  sisters: string;
  marriedSisters: string;
  familyCountryId: string;
  familyStateId: string;
  familyCityId: string;
  contactAddress: string;
  aboutFamily: string;
}

interface DynamicFieldsState {
  showSect: boolean;
  showCaste: boolean;
  showJamat: boolean;
  showManglik: boolean;
}

interface PhotoUploadState {
  mainPhoto: UploadedFile | null;
  mainPhotoPreview: string | null;
  galleryPhotos: UploadedFile[];
  galleryPreviews: string[];
}

const MAX_GALLERY_PHOTOS = 4;

// ============ Static Options ============

const TOTAL_PHOTO_SLOTS = 5;

// ============ Initial State ============

const initialFormData: ProfileFormData = {
  // Personal Info
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
  phoneCountryCode: DEFAULT_PHONE_COUNTRY_CODE,
  phoneNumber: '',
  phoneE164: '',
  gender: '',
  dateOfBirth: '',
  // Profile Details
  motherTongueId: '',
  religionId: '',
  maritalStatusId: '',
  heightId: '',
  // New Physical Fields
  bodyTypeId: '',
  weight: '',
  complexionId: '',
  bloodGroupId: '',
  disabilityId: '',
  // Dynamic Religion Fields
  sectId: '',
  casteId: '',
  jamatId: '',
  casteNoBar: false,
  manglikId: '',
  horoscopeNecessity: '',
  // Career Details
  countryId: '',
  stateId: '',
  cityId: '',
  degreeId: '',
  employedInId: '',
  occupationId: '',
  income_currency_id: '',
  annualIncome: '',
  aboutYou: '',
  // Lifestyle & Family
  drinkingHabitId: '',
  smokingHabitId: '',
  dietId: '',
  familyTypeId: '',
  fatherOccupationId: '',
  motherOccupationId: '',
  brothers: '',
  marriedBrothers: '',
  sisters: '',
  marriedSisters: '',
  familyCountryId: '',
  familyStateId: '',
  familyCityId: '',
  contactAddress: '',
  aboutFamily: '',
};

const initialDynamicFields: DynamicFieldsState = {
  showSect: false,
  showCaste: false,
  showJamat: false,
  showManglik: false,
};

// ============ Component ============

export const CreateUser: React.FC = () => {
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState<ProfileFormData>(initialFormData);
  const [dynamicFields, setDynamicFields] = useState<DynamicFieldsState>(initialDynamicFields);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailValidation = useEmailValidation();

  const fullPhoneNumber = useMemo(
    () =>
      formData.phoneE164 ||
      buildFullPhoneNumber(formData.phoneCountryCode, formData.phoneNumber),
    [formData.phoneE164, formData.phoneCountryCode, formData.phoneNumber]
  );

  const profileValidation = useProfileFormValidation(
    formData,
    dynamicFields,
    fullPhoneNumber
  );

  // Loading states for dependent dropdowns
  const [isLoadingSects, setIsLoadingSects] = useState(false);
  const [isLoadingCastes, setIsLoadingCastes] = useState(false);
  const [isLoadingJamats, setIsLoadingJamats] = useState(false);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [isLoadingOccupations, setIsLoadingOccupations] = useState(false);
  // Family location loading states (separate from career)
  const [isLoadingFamilyStates, setIsLoadingFamilyStates] = useState(false);
  const [isLoadingFamilyCities, setIsLoadingFamilyCities] = useState(false);

  // Family location data (separate from career location)
  const [familyStates, setFamilyStates] = useState<SelectOption[]>([]);
  const [familyCities, setFamilyCities] = useState<SelectOption[]>([]);

  // Income currencies
  const [incomeCurrencies, setIncomeCurrencies] = useState<SelectOption[]>([]);
  const [isLoadingCurrencies, setIsLoadingCurrencies] = useState(false);

  // Photo upload state
  const [photoState, setPhotoState] = useState<PhotoUploadState>({
    mainPhoto: null,
    mainPhotoPreview: null,
    galleryPhotos: [],
    galleryPreviews: [],
  });
  const [isUploadingMainPhoto, setIsUploadingMainPhoto] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);

  // File input refs
  const mainPhotoInputRef = useRef<HTMLInputElement>(null);

  // Get shared lookup data from store
  const {
    // Profile lookups
    motherTongues,
    heights,
    religions,
    maritalStatuses,
    bodyTypes,
    complexions,
    bloodGroups,
    disabilities,
    sects,
    castes,
    jamats,
    countries,
    states,
    cities,
    degrees,
    employedIn,
    occupations,
    drinkingHabits,
    smokingHabits,
    diets,
    familyTypes,
    fatherOccupations,
    motherOccupations,
    // Actions
    fetchSectsWithMeta,
    fetchCastes,
    fetchJamats,
    fetchStates,
    fetchCities,
    fetchOccupations,
    getReligionMeta,
    getSectMeta,
    clearDependentLookups,
  } = useSharedStore();

  // ============ Profile Details Handlers ============

  const handleReligionChange = useCallback(async (religionId: string) => {
    setFormData(prev => ({
      ...prev,
      religionId,
      sectId: '',
      casteId: '',
      jamatId: '',
      casteNoBar: false,
      manglikId: '',
    }));
    profileValidation.touchOnChange('religionId');

    clearDependentLookups('sects');
    clearDependentLookups('sectsWithMeta');
    clearDependentLookups('castes');
    clearDependentLookups('jamats');

    if (!religionId) {
      setDynamicFields(initialDynamicFields);
      return;
    }

    const religionMeta = getReligionMeta(religionId);
    if (!religionMeta) {
      setDynamicFields(initialDynamicFields);
      return;
    }

    const { have_sect, have_caste, have_manglik } = religionMeta;

    setDynamicFields({
      showSect: have_sect,
      showCaste: !have_sect && have_caste,
      showJamat: false,
      showManglik: have_manglik,
    });

    if (have_sect) {
      setIsLoadingSects(true);
      try {
        await fetchSectsWithMeta(religionId);
      } finally {
        setIsLoadingSects(false);
      }
    }

    if (!have_sect && have_caste) {
      setIsLoadingCastes(true);
      try {
        await fetchCastes(religionId);
      } finally {
        setIsLoadingCastes(false);
      }
    }
  }, [getReligionMeta, fetchSectsWithMeta, fetchCastes, clearDependentLookups, profileValidation]);

  const handleSectChange = useCallback(async (sectId: string) => {
    setFormData(prev => ({
      ...prev,
      sectId,
      casteId: '',
      jamatId: '',
      casteNoBar: false,
    }));
    profileValidation.touchOnChange('sectId');

    clearDependentLookups('castes');
    clearDependentLookups('jamats');

    if (!sectId || !formData.religionId) {
      setDynamicFields(prev => ({ ...prev, showCaste: false, showJamat: false }));
      return;
    }

    const sectMeta = getSectMeta(sectId);
    const hasJamat = sectMeta?.have_jamat ?? false;

    setDynamicFields(prev => ({ ...prev, showCaste: true, showJamat: hasJamat }));

    setIsLoadingCastes(true);
    try {
      await fetchCastes(formData.religionId, sectId);
    } finally {
      setIsLoadingCastes(false);
    }

    if (hasJamat) {
      setIsLoadingJamats(true);
      try {
        await fetchJamats(sectId);
      } finally {
        setIsLoadingJamats(false);
      }
    }
  }, [formData.religionId, getSectMeta, fetchCastes, fetchJamats, clearDependentLookups, profileValidation]);

  // ============ Career Details Handlers ============

  const handleCountryChange = useCallback(async (countryId: string) => {
    setFormData(prev => ({
      ...prev,
      countryId,
      stateId: '',
      cityId: '',
    }));
    profileValidation.touchOnChange('countryId');

    clearDependentLookups('states');
    clearDependentLookups('cities');

    if (!countryId) return;

    setIsLoadingStates(true);
    try {
      await fetchStates(countryId);
    } finally {
      setIsLoadingStates(false);
    }
  }, [fetchStates, clearDependentLookups, profileValidation]);

  const handleStateChange = useCallback(async (stateId: string) => {
    setFormData(prev => ({
      ...prev,
      stateId,
      cityId: '',
    }));
    profileValidation.touchOnChange('stateId');

    clearDependentLookups('cities');

    if (!stateId) return;

    setIsLoadingCities(true);
    try {
      await fetchCities(stateId);
    } finally {
      setIsLoadingCities(false);
    }
  }, [fetchCities, clearDependentLookups, profileValidation]);

  const handleEmployedInChange = useCallback(async (employedInId: string) => {
    setFormData(prev => ({
      ...prev,
      employedInId,
      occupationId: '',
    }));
    profileValidation.touchOnChange('employedInId');

    clearDependentLookups('occupations');

    if (!employedInId) return;

    setIsLoadingOccupations(true);
    try {
      await fetchOccupations(employedInId);
    } finally {
      setIsLoadingOccupations(false);
    }
  }, [fetchOccupations, clearDependentLookups, profileValidation]);

  // ============ Lifestyle & Family Handlers ============

  const handleSiblingCountChange = useCallback((
    field: 'brothers' | 'sisters',
    value: string
  ) => {
    const sanitized = value.replace(/[^0-9]/g, '');
    setFormData(prev => ({ ...prev, [field]: sanitized }));
  }, []);

  // Family location handlers (separate cascade from career location)
  const handleFamilyCountryChange = useCallback(async (familyCountryId: string) => {
    setFormData(prev => ({
      ...prev,
      familyCountryId,
      familyStateId: '',
      familyCityId: '',
    }));

    setFamilyStates([]);
    setFamilyCities([]);

    if (!familyCountryId) return;

    setIsLoadingFamilyStates(true);
    try {
      // Use the sharedService directly for family location
      const { sharedService } = await import('../../services/sharedService');
      const statesData = await sharedService.getStates(familyCountryId);
      setFamilyStates(statesData);
    } finally {
      setIsLoadingFamilyStates(false);
    }
  }, []);

  const handleFamilyStateChange = useCallback(async (familyStateId: string) => {
    setFormData(prev => ({
      ...prev,
      familyStateId,
      familyCityId: '',
    }));

    setFamilyCities([]);

    if (!familyStateId) return;

    setIsLoadingFamilyCities(true);
    try {
      const { sharedService } = await import('../../services/sharedService');
      const citiesData = await sharedService.getCities(familyStateId);
      setFamilyCities(citiesData);
    } finally {
      setIsLoadingFamilyCities(false);
    }
  }, []);

  // ============ Fetch Income Currencies ============
  useEffect(() => {
    const fetchCurrencies = async () => {
      setIsLoadingCurrencies(true);
      try {
        const currencies = await sharedService.getCurrencies();
        setIncomeCurrencies(currencies);
      } catch (err) {
        console.error('Failed to fetch income currencies:', err);
      } finally {
        setIsLoadingCurrencies(false);
      }
    };
    fetchCurrencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============ Generic Handler ============

  const handleInputChange = useCallback((field: keyof ProfileFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
    if (field in PROFILE_FIELD_LABELS) {
      profileValidation.touchOnChange(field as ProfileValidationField);
    }
  }, [profileValidation]);

  const handlePhoneChange = useCallback((value: string) => {
    const { countryCode, localNumber } = parsePhoneNumber(value);
    setFormData((prev) => ({
      ...prev,
      phoneE164: value,
      phoneCountryCode: countryCode,
      phoneNumber: localNumber,
    }));
    setError(null);
  }, []);

  // ============ Annual Income Handler ============

  const handleAnnualIncomeChange = useCallback((value: string) => {
    // Allow only numbers
    const sanitized = value.replace(/[^0-9]/g, '');
    setFormData(prev => ({ ...prev, annualIncome: sanitized }));
    setError(null);
    profileValidation.touchOnChange('annualIncome');
  }, [profileValidation]);

  // ============ Weight Validation ============

  const handleWeightChange = useCallback((value: string) => {
    // Allow only numbers and single decimal point
    const sanitized = value.replace(/[^0-9.]/g, '');

    // Prevent multiple decimal points
    const parts = sanitized.split('.');
    const cleanValue = parts.length > 2
      ? parts[0] + '.' + parts.slice(1).join('')
      : sanitized;

    // Limit decimal places to 1
    const finalValue = cleanValue.includes('.')
      ? cleanValue.split('.')[0] + '.' + (cleanValue.split('.')[1]?.slice(0, 1) || '')
      : cleanValue;

    setFormData(prev => ({ ...prev, weight: finalValue }));
    setError(null);
  }, []);

  const validateWeight = useCallback((value: string): string | null => {
    if (!value) return null; // Weight is optional

    const num = parseFloat(value);
    if (isNaN(num)) return 'Please enter a valid number';
    if (num <= 0) return 'Weight must be greater than 0';
    if (num > 300) return 'Weight cannot exceed 300 kg';

    return null;
  }, []);

  const [weightError, setWeightError] = useState<string | null>(null);

  const handleWeightBlur = useCallback(() => {
    const error = validateWeight(formData.weight);
    setWeightError(error);
  }, [formData.weight, validateWeight]);

  // ============ Photo Upload Handlers ============

  const validateImageFile = useCallback((file: File): string | null => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      return 'Only JPEG, PNG, and WebP images are allowed';
    }
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return 'Image size must be less than 5MB';
    }
    return null;
  }, []);

  const createPreviewUrl = useCallback((file: File): string => {
    return URL.createObjectURL(file);
  }, []);

  const uploadMainPhoto = useCallback(
    async (file: File) => {
      setPhotoError(null);
      setIsUploadingMainPhoto(true);

      const previewUrl = createPreviewUrl(file);
      setPhotoState((prev) => ({
        ...prev,
        mainPhotoPreview: previewUrl,
      }));

      try {
        const uploadedFiles = await sharedService.uploadFiles([file]);

        if (uploadedFiles.length > 0) {
          const uploadedFile = uploadedFiles[0];
          setPhotoState((prev) => ({
            ...prev,
            mainPhoto: uploadedFile,
            mainPhotoPreview: uploadedFile.url,
          }));
        }
      } catch (err) {
        console.error('Failed to upload main photo:', err);
        setPhotoError('Failed to upload photo. Please try again.');
        setPhotoState((prev) => ({
          ...prev,
          mainPhotoPreview: prev.mainPhoto?.url || null,
        }));
        throw err;
      } finally {
        setIsUploadingMainPhoto(false);
      }
    },
    [createPreviewUrl]
  );

  const {
    isCropOpen,
    cropImageSrc,
    cropFileMeta,
    isApplyingCrop,
    closeCrop,
    handleCropApply,
    handleFileInputChange: handleMainPhotoSelect,
  } = usePhotoCropFlow({
    validateImageFile,
    onUpload: uploadMainPhoto,
    onValidationError: setPhotoError,
  });

  const uploadGalleryFileAtSlot = useCallback(
    async (file: File, slotIndex: number) => {
      setPhotoError(null);
      const previewUrl = createPreviewUrl(file);

      setPhotoState((prev) => {
        const galleryPreviews = [...prev.galleryPreviews];
        galleryPreviews[slotIndex] = previewUrl;
        return { ...prev, galleryPreviews };
      });

      try {
        const uploadedFiles = await sharedService.uploadFiles([file]);
        const uploadedFile = uploadedFiles[0];
        if (!uploadedFile?.id) {
          throw new Error('Failed to upload photo.');
        }

        setPhotoState((prev) => {
          const galleryPhotos = [...prev.galleryPhotos];
          const galleryPreviews = [...prev.galleryPreviews];
          galleryPhotos[slotIndex] = uploadedFile;
          galleryPreviews[slotIndex] = uploadedFile.url;
          return { ...prev, galleryPhotos, galleryPreviews };
        });
      } catch (err) {
        console.error('Failed to upload gallery photo:', err);
        setPhotoError('Failed to upload photo. Please try again.');
        setPhotoState((prev) => ({
          ...prev,
          galleryPreviews: prev.galleryPhotos.map((photo, index) => photo?.url ?? null),
        }));
        throw err;
      }
    },
    [createPreviewUrl]
  );

  const {
    galleryInputRef,
    openGalleryPicker,
    handleGalleryFileChange,
    isGallerySlotUploading,
    isCropOpen: isGalleryCropOpen,
    cropImageSrc: galleryCropImageSrc,
    cropFileMeta: galleryCropFileMeta,
    isApplyingCrop: isApplyingGalleryCrop,
    closeCrop: closeGalleryCrop,
    handleCropApply: handleGalleryCropApply,
  } = useGalleryPhotoCropUpload({
    validateImageFile,
    uploadGalleryFile: uploadGalleryFileAtSlot,
    onValidationError: setPhotoError,
  });

  const handleRemoveMainPhoto = useCallback(() => {
    setPhotoState(prev => ({
      ...prev,
      mainPhoto: null,
      mainPhotoPreview: null,
    }));
    setPhotoError(null);
  }, []);

  const handleRemoveGalleryPhoto = useCallback((index: number) => {
    setPhotoState(prev => ({
      ...prev,
      galleryPhotos: prev.galleryPhotos.filter((_, i) => i !== index),
      galleryPreviews: prev.galleryPreviews.filter((_, i) => i !== index),
    }));
  }, []);

  const triggerMainPhotoInput = useCallback(() => {
    mainPhotoInputRef.current?.click();
  }, []);

  const handleGallerySlotClick = useCallback((slotIndex: number) => {
    if (photoState.galleryPreviews[slotIndex]) {
      return;
    }
    if (photoState.galleryPhotos.length >= MAX_GALLERY_PHOTOS) {
      setPhotoError(`Maximum ${MAX_GALLERY_PHOTOS} gallery photos allowed`);
      return;
    }
    openGalleryPicker(slotIndex);
  }, [openGalleryPicker, photoState.galleryPhotos.length, photoState.galleryPreviews]);

  // ============ Form Submission ============

  const handleSubmit = async () => {
    const emailResult = emailValidation.validate(formData.email);
    if (!emailResult.valid) {
      return;
    }

    if (!profileValidation.validateAll()) {
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Build flat payload matching API structure
      const createData: CreateUserRequest = {
        // Personal Info
        firstName: formData.firstName,
        ...(formData.middleName && { middleName: formData.middleName }),
        lastName: formData.lastName,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        phoneNumber: fullPhoneNumber,
        email: emailResult.trimmed,

        // Photos — always send explicit values
        ...buildProfilePhotoPayload(photoState),

        // Religion & Caste
        motherTongueId: parseInt(formData.motherTongueId),
        religionId: parseInt(formData.religionId),
        ...(formData.sectId && { sectId: parseInt(formData.sectId) }),
        ...(formData.jamatId && { jamatId: parseInt(formData.jamatId) }),
        ...(formData.casteId && { casteId: parseInt(formData.casteId) }),
        haveCasteBar: formData.casteNoBar,

        // Profile Details (Required)
        maratialStatusId: parseInt(formData.maritalStatusId),
        heightId: parseInt(formData.heightId),
        ...(formData.manglikId && { manglik: formData.manglikId }),
        isHoroscopeNecessary: formData.horoscopeNecessity === 'must',

        // Physical Details (Optional)
        ...(formData.bodyTypeId && { bodyTypeId: parseInt(formData.bodyTypeId) }),
        ...(formData.weight && { weight: parseFloat(formData.weight) }),
        ...(formData.complexionId && { complexionId: parseInt(formData.complexionId) }),
        ...(formData.bloodGroupId && { bloodGroupId: parseInt(formData.bloodGroupId) }),
        ...(formData.disabilityId && { disabilityId: parseInt(formData.disabilityId) }),

        // Location
        countryId: parseInt(formData.countryId),
        stateId: parseInt(formData.stateId),
        cityId: parseInt(formData.cityId),

        // Education & Career
        highestDegreeId: parseInt(formData.degreeId),
        employedInId: parseInt(formData.employedInId),
        occupationId: parseInt(formData.occupationId),
        income_currency_id: parseInt(formData.income_currency_id),
        annualIncome: parseInt(formData.annualIncome),
        ...(formData.aboutYou.trim() && { aboutYourself: formData.aboutYou.trim() }),

        // Lifestyle
        drinkingHabitId: parseInt(formData.drinkingHabitId),
        smokingHabitId: parseInt(formData.smokingHabitId),
        dietId: parseInt(formData.dietId),

        // Family (Optional)
        ...(formData.familyTypeId && { familyTypeId: parseInt(formData.familyTypeId) }),
        ...(formData.fatherOccupationId && { fatherOccupationId: parseInt(formData.fatherOccupationId) }),
        ...(formData.motherOccupationId && { motherOccupationId: parseInt(formData.motherOccupationId) }),
        ...(formData.brothers && { brothers: formData.brothers }),
        ...(formData.marriedBrothers && { brothersMarried: formData.marriedBrothers }),
        ...(formData.sisters && { sisters: formData.sisters }),
        ...(formData.marriedSisters && { sistersMarried: formData.marriedSisters }),
        ...(formData.familyCountryId && { familyLivingCountryId: parseInt(formData.familyCountryId) }),
        ...(formData.familyStateId && { familyLivingStateId: parseInt(formData.familyStateId) }),
        ...(formData.familyCityId && { familyLivingCityId: parseInt(formData.familyCityId) }),
        ...(formData.contactAddress.trim() && { address: formData.contactAddress.trim() }),
        ...(formData.aboutFamily.trim() && { aboutFamily: formData.aboutFamily.trim() }),
      };

      console.log('Creating user with data:', createData);
      const response = await userService.createUser(createData);
      console.log('User created successfully:', response);

      // Clear all dependent lookups
      clearDependentLookups('sects');
      clearDependentLookups('sectsWithMeta');
      clearDependentLookups('castes');
      clearDependentLookups('jamats');
      clearDependentLookups('states');
      clearDependentLookups('cities');
      clearDependentLookups('occupations');

      navigate('/users');
    } catch (err) {
      console.error('Failed to create user:', err);
      const fieldErrors = getFieldErrorsFromApi(err);
      if (fieldErrors.email) {
        emailValidation.setError(fieldErrors.email);
      } else {
        setError(formatApiError(err) || 'Failed to create user');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const canSubmit =
    emailValidation.isValid(formData.email.trim()) &&
    !emailValidation.error &&
    profileValidation.isValid &&
    !isUploadingMainPhoto &&
    !isApplyingCrop &&
    !isApplyingGalleryCrop;

  const getSubmitDisabledReason = (): string | undefined => {
    if (isSaving) return 'Creating...';
    if (emailValidation.error) return emailValidation.error;
    return profileValidation.getSubmitDisabledReason();
  };

  // ============ Navigation ============

  const handleBack = useCallback(() => {
    navigate('/users');
  }, [navigate]);

  const handleCancel = useCallback(() => {
    // Clear all dependent lookups before navigating
    clearDependentLookups('sects');
    clearDependentLookups('sectsWithMeta');
    clearDependentLookups('castes');
    clearDependentLookups('jamats');
    clearDependentLookups('states');
    clearDependentLookups('cities');
    clearDependentLookups('occupations');
    navigate('/users');
  }, [navigate, clearDependentLookups]);

  // ============ Render ============

  return (
    <div className="create-user-page create-user-page--edit">
      <PageHeader
        title="Create Client Profile"
        subtitle="Add client information across photos, details, career, and family."
        backButton={{ label: 'Back to Client List', onClick: handleBack }}
      />

      {error && (
        <div className="create-user-error" role="alert">
          {error}
        </div>
      )}

      <div className="create-user-content create-user-content--edit-mode">
        <input
          ref={mainPhotoInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleMainPhotoSelect}
          style={{ display: 'none' }}
        />
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleGalleryFileChange}
          style={{ display: 'none' }}
        />

        <InfoCard
          title="1. Profile Photos"
          iconSrc={CREATE_USER_SECTION_ICONS.camera}
          iconAlt=""
          className="create-user-section create-user-section--photos"
        >
          <div className="photo-upload-section">
            {photoError && <div className="photo-error">{photoError}</div>}

            <div className="photo-slots-row">
              {Array.from({ length: TOTAL_PHOTO_SLOTS }).map((_, index) => {
                const isPrimary = index === 0;
                const galleryIndex = index - 1;
                const galleryPreview = !isPrimary
                  ? photoState.galleryPreviews[galleryIndex]
                  : null;
                const preview = isPrimary
                  ? photoState.mainPhotoPreview
                  : galleryPreview;
                const isUploading = isPrimary
                  ? isUploadingMainPhoto || isApplyingCrop
                  : isGallerySlotUploading(galleryIndex);

                return (
                  <div
                    key={index}
                    className={`photo-slot ${isPrimary ? 'photo-slot--primary primary-photo-slot' : ''} ${preview ? 'photo-slot--filled' : ''} ${isUploading ? 'photo-slot--uploading' : ''}`}
                    onClick={() => {
                      if (isUploading) return;
                      if (isPrimary) {
                        triggerMainPhotoInput();
                      } else {
                        handleGallerySlotClick(galleryIndex);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (isPrimary) triggerMainPhotoInput();
                        else handleGallerySlotClick(galleryIndex);
                      }
                    }}
                  >
                    {isUploading ? (
                      <Spinner size="small" />
                    ) : preview ? (
                      <>
                        <img src={preview} alt="" className="photo-slot__image" />
                        <button
                          type="button"
                          className="photo-slot__remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isPrimary) handleRemoveMainPhoto();
                            else handleRemoveGalleryPhoto(galleryIndex);
                          }}
                          aria-label="Remove photo"
                        >
                          <TrashIcon />
                        </button>
                      </>
                    ) : (
                      <div className="photo-slot__placeholder">
                        {isPrimary ? (
                          <>
                            <img
                              src={CREATE_USER_SECTION_ICONS.camera}
                              alt=""
                              className="photo-slot__asset-icon"
                            />
                            <span className="photo-slot__label">Primary Photo*</span>
                          </>
                        ) : (
                          <PlusIcon className="photo-slot__icon" />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </InfoCard>

        <InfoCard
          title="2. Profile Details"
          iconSrc={CREATE_USER_SECTION_ICONS.person}
          iconAlt=""
          className="create-user-section"
        >
          <div className="profile-form">
            {/* Row 1: Names (3 columns) */}
            <div className="form-row form-row--3">
              <Input
                label="First Name"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                onBlur={() => profileValidation.touch('firstName')}
                error={profileValidation.showError('firstName')}
                placeholder="John"
                required
              />
              <Input
                label="Middle Name"
                value={formData.middleName}
                onChange={(e) => handleInputChange('middleName', e.target.value)}
                placeholder="Quincy"
              />
              <Input
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                onBlur={() => profileValidation.touch('lastName')}
                error={profileValidation.showError('lastName')}
                placeholder="Doe"
                required
              />
            </div>

            <div className="form-row form-row--3">
              <PhoneInput
                label="Contact Number"
                value={formData.phoneE164 || undefined}
                onChange={handlePhoneChange}
                onBlur={() => profileValidation.touch('phoneNumber')}
                error={profileValidation.showError('phoneNumber')}
                placeholder="9876543210"
                defaultCountry="IN"
                international
                required
              />
              <Input
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={(e) => {
                  handleInputChange('email', e.target.value);
                  emailValidation.onChange(e.target.value);
                }}
                onBlur={() => emailValidation.onBlur(formData.email)}
                error={emailValidation.error}
                placeholder="john.doe@example.com"
                required
              />
              <Select
                label="Gender"
                placeholder="Select Gender"
                value={formData.gender}
                onChange={(value) => handleInputChange('gender', value)}
                options={genderOptions}
                error={profileValidation.showError('gender')}
                required
              />
            </div>

            <div className="form-row form-row--3">
              <Input
                label="Date of Birth"
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                onBlur={() => profileValidation.touch('dateOfBirth')}
                error={profileValidation.showError('dateOfBirth')}
                max={getMaxDateOfBirthForMinAge()}
                required
              />
              <Select
                label="Mother Tongue"
                placeholder="Select Language"
                value={formData.motherTongueId}
                onChange={(value) => handleInputChange('motherTongueId', value)}
                groups={motherTongues}
                searchable
                error={profileValidation.showError('motherTongueId')}
                required
              />
              <Select
                label="Religion"
                placeholder="Select Religion"
                value={formData.religionId}
                onChange={handleReligionChange}
                options={religions}
                error={profileValidation.showError('religionId')}
                required
              />
            </div>

            {dynamicFields.showSect && (
              <div className="form-row form-row--3">
                <Select
                  label="Sect"
                  placeholder={isLoadingSects ? 'Loading...' : 'Select Sect'}
                  value={formData.sectId}
                  onChange={handleSectChange}
                  options={sects}
                  disabled={isLoadingSects || sects.length === 0}
                  error={profileValidation.showError('sectId')}
                  required
                />
              </div>
            )}

            {/* Row 4: Caste with checkbox */}
            {(dynamicFields.showCaste || (dynamicFields.showSect && formData.sectId)) && (
              <div className="form-row form-row--2">
                <Select
                  label="Caste"
                  placeholder={isLoadingCastes ? 'Loading...' : 'Caste'}
                  value={formData.casteId}
                  onChange={(value) => handleInputChange('casteId', value)}
                  options={castes}
                  searchable
                  disabled={isLoadingCastes || castes.length === 0 || formData.casteNoBar}
                  error={profileValidation.showError('casteId')}
                  required={!formData.casteNoBar}
                />
                <div className="checkbox-field">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.casteNoBar}
                      onChange={(e) => {
                        handleInputChange('casteNoBar', e.target.checked);
                        if (e.target.checked) {
                          handleInputChange('casteId', '');
                        }
                      }}
                    />
                    <span>Caste no bar (I am open to marry people of all castes)</span>
                  </label>
                </div>
              </div>
            )}

            {/* Row 5: Jamat */}
            {dynamicFields.showJamat && (
              <div className="form-row form-row--2">
                <Select
                  label="Jamat"
                  placeholder={isLoadingJamats ? 'Loading...' : 'Jamat'}
                  value={formData.jamatId}
                  onChange={(value) => handleInputChange('jamatId', value)}
                  options={jamats}
                  disabled={isLoadingJamats || jamats.length === 0 || !formData.sectId}
                  error={profileValidation.showError('jamatId')}
                  required
                />
              </div>
            )}

            <div className="form-row form-row--3">
              {dynamicFields.showManglik && (
                <Select
                  label="Are you manglik?"
                  placeholder="No"
                  value={formData.manglikId}
                  onChange={(value) => handleInputChange('manglikId', value)}
                  options={manglikOptions}
                  error={profileValidation.showError('manglikId')}
                  required
                />
              )}
              <Select
                label="Horoscope?"
                placeholder="Select"
                value={formData.horoscopeNecessity}
                onChange={(value) => handleInputChange('horoscopeNecessity', value)}
                options={horoscopeOptions}
                error={profileValidation.showError('horoscopeNecessity')}
                required
              />
            </div>

            <div className="form-row form-row--3">
              <Select
                label="Marital Status"
                placeholder="Never Married"
                value={formData.maritalStatusId}
                onChange={(value) => handleInputChange('maritalStatusId', value)}
                options={maritalStatuses}
                error={profileValidation.showError('maritalStatusId')}
                required
              />
              <Select
                label="Height"
                placeholder="Select Height"
                value={formData.heightId}
                onChange={(value) => handleInputChange('heightId', value)}
                options={heights}
                searchable
                error={profileValidation.showError('heightId')}
                required
              />
              <Select
                label="Body Type"
                placeholder="Average"
                value={formData.bodyTypeId}
                onChange={(value) => handleInputChange('bodyTypeId', value)}
                options={bodyTypes}
              />
            </div>

            <div className="form-row form-row--3">
              <Input
                label="Weight (kg)"
                type="text"
                inputMode="decimal"
                value={formData.weight}
                onChange={(e) => handleWeightChange(e.target.value)}
                onBlur={handleWeightBlur}
                placeholder="70"
                error={weightError ?? undefined}
              />
              <Select
                label="Complexion"
                placeholder="Fair"
                value={formData.complexionId}
                onChange={(value) => handleInputChange('complexionId', value)}
                options={complexions}
              />
              <Select
                label="Blood Group"
                placeholder="O+"
                value={formData.bloodGroupId}
                onChange={(value) => handleInputChange('bloodGroupId', value)}
                options={bloodGroups}
              />
            </div>

            <div className="form-row form-row--3">
              <Select
                label="Disability Status"
                placeholder="None"
                value={formData.disabilityId}
                onChange={(value) => handleInputChange('disabilityId', value)}
                options={disabilities}
              />
            </div>
          </div>
        </InfoCard>

        <InfoCard
          title="3. Career Details"
          iconSrc={CREATE_USER_SECTION_ICONS.workBag}
          iconAlt=""
          className="create-user-section"
        >
          <div className="profile-form">
            <div className="form-row form-row--3">
              <Select
                label="Country"
                placeholder="Select Country"
                value={formData.countryId}
                onChange={handleCountryChange}
                options={countries}
                searchable
                error={profileValidation.showError('countryId')}
                required
              />
              <Select
                label="Employed In"
                placeholder="Private Sector"
                value={formData.employedInId}
                onChange={handleEmployedInChange}
                options={employedIn}
                error={profileValidation.showError('employedInId')}
                required
              />
              <Select
                label="State"
                placeholder={isLoadingStates ? 'Loading...' : 'Maharashtra'}
                value={formData.stateId}
                onChange={handleStateChange}
                options={states}
                searchable
                disabled={!formData.countryId || isLoadingStates}
                error={profileValidation.showError('stateId')}
                required
              />
            </div>

            <div className="form-row form-row--3">
              <Select
                label="City"
                placeholder={isLoadingCities ? 'Loading...' : 'Mumbai'}
                value={formData.cityId}
                onChange={(value) => handleInputChange('cityId', value)}
                options={cities}
                searchable
                disabled={!formData.stateId || isLoadingCities}
                error={profileValidation.showError('cityId')}
                required
              />
              <Select
                label="Occupation"
                placeholder={isLoadingOccupations ? 'Loading...' : 'Software Engineer'}
                value={formData.occupationId}
                onChange={(value) => handleInputChange('occupationId', value)}
                groups={occupations}
                searchable
                disabled={!formData.employedInId || isLoadingOccupations}
                error={profileValidation.showError('occupationId')}
                required
              />
              <Select
                label="Highest Degree"
                placeholder="B.Tech / MBA / MBBS"
                value={formData.degreeId}
                onChange={(value) => handleInputChange('degreeId', value)}
                groups={degrees}
                searchable
                error={profileValidation.showError('degreeId')}
                required
              />
            </div>

            <div className="form-row form-row--1">
              <div
                className={`income-field${
                  profileValidation.showError('income_currency_id') ||
                  profileValidation.showError('annualIncome')
                    ? ' income-field--error'
                    : ''
                }`}
              >
                <label className="income-field__label">
                  Annual Income <span className="required-mark">*</span>
                </label>
                <div className="income-field__inputs">
                  <Select
                    placeholder={isLoadingCurrencies ? 'Loading...' : 'Currency'}
                    value={formData.income_currency_id}
                    onChange={(value) => handleInputChange('income_currency_id', value)}
                    options={incomeCurrencies}
                    disabled={isLoadingCurrencies}
                    searchable={false}
                    fullWidth={false}
                    className="income-field__currency"
                    required
                  />
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={formData.annualIncome}
                    onChange={(e) => handleAnnualIncomeChange(e.target.value)}
                    onBlur={() => profileValidation.touch('annualIncome')}
                    placeholder="1,200,000"
                    className="income-field__amount"
                    fullWidth={false}
                    required
                  />
                </div>
                {(profileValidation.showError('income_currency_id') ||
                  profileValidation.showError('annualIncome')) && (
                  <span className="income-field__error" role="alert">
                    {profileValidation.showError('income_currency_id') ||
                      profileValidation.showError('annualIncome')}
                  </span>
                )}
              </div>
            </div>

            <div className="form-row form-row--1">
              <div className="textarea-field">
                <label className="textarea-label">About You</label>
                <textarea
                  value={formData.aboutYou}
                  onChange={(e) => handleInputChange('aboutYou', e.target.value)}
                  placeholder="Describe yourself, your interests, and what you are looking for in a partner..."
                  rows={4}
                />
              </div>
            </div>
          </div>
        </InfoCard>

        <InfoCard
          title="4. Lifestyle & Family"
          iconSrc={CREATE_USER_SECTION_ICONS.lifestyleTree}
          iconAlt=""
          className="create-user-section"
        >
          <div className="profile-form">
            <div className="form-row form-row--3">
              <Select
                label="Do you Drink?"
                placeholder="No"
                value={formData.drinkingHabitId}
                onChange={(value) => handleInputChange('drinkingHabitId', value)}
                options={drinkingHabits}
                error={profileValidation.showError('drinkingHabitId')}
                required
              />
              <Select
                label="Do you Smoke?"
                placeholder="No"
                value={formData.smokingHabitId}
                onChange={(value) => handleInputChange('smokingHabitId', value)}
                options={smokingHabits}
                error={profileValidation.showError('smokingHabitId')}
                required
              />
              <Select
                label="Diet"
                placeholder="Vegetarian"
                value={formData.dietId}
                onChange={(value) => handleInputChange('dietId', value)}
                options={diets}
                error={profileValidation.showError('dietId')}
                required
              />
            </div>

            <div className="form-row form-row--3">
              <Select
                label="Family Type"
                placeholder="Joint Family"
                value={formData.familyTypeId}
                onChange={(value) => handleInputChange('familyTypeId', value)}
                options={familyTypes}
              />
              <Select
                label="Family Living In — Country"
                placeholder="Country"
                value={formData.familyCountryId}
                onChange={handleFamilyCountryChange}
                options={countries}
                searchable
              />
              <Select
                label="State"
                placeholder={isLoadingFamilyStates ? 'Loading...' : 'State'}
                value={formData.familyStateId}
                onChange={handleFamilyStateChange}
                options={familyStates}
                searchable
                disabled={!formData.familyCountryId || isLoadingFamilyStates}
              />
            </div>

            <div className="form-row form-row--3">
              <Select
                label="City"
                placeholder={isLoadingFamilyCities ? 'Loading...' : 'City'}
                value={formData.familyCityId}
                onChange={(value) => handleInputChange('familyCityId', value)}
                options={familyCities}
                searchable
                disabled={!formData.familyStateId || isLoadingFamilyCities}
              />
              <Select
                label="Father's Occupation"
                placeholder="Retired / Business"
                value={formData.fatherOccupationId}
                onChange={(value) => handleInputChange('fatherOccupationId', value)}
                options={fatherOccupations}
              />
              <Select
                label="Mother's Occupation"
                placeholder="Homemaker / Teacher"
                value={formData.motherOccupationId}
                onChange={(value) => handleInputChange('motherOccupationId', value)}
                options={motherOccupations}
              />
            </div>

            <div className="form-row form-row--1">
              <div className="textarea-field">
                <label className="textarea-label">Full Contact Address</label>
                <textarea
                  value={formData.contactAddress}
                  onChange={(e) => handleInputChange('contactAddress', e.target.value)}
                  placeholder="Enter detailed residence address..."
                  rows={3}
                />
              </div>
            </div>

            <div className="form-row form-row--2">
              <Input
                label="Brothers Count"
                type="text"
                inputMode="numeric"
                value={formData.brothers}
                onChange={(e) => handleSiblingCountChange('brothers', e.target.value)}
                placeholder="0"
              />
              <Input
                label="Sisters Count"
                type="text"
                inputMode="numeric"
                value={formData.sisters}
                onChange={(e) => handleSiblingCountChange('sisters', e.target.value)}
                placeholder="0"
              />
            </div>

            <div className="form-row form-row--1">
              <div className="textarea-field">
                <label className="textarea-label">About Family</label>
                <textarea
                  value={formData.aboutFamily}
                  onChange={(e) => handleInputChange('aboutFamily', e.target.value)}
                  placeholder="Briefly describe your family values, background, and members..."
                  rows={4}
                />
              </div>
            </div>
          </div>
        </InfoCard>

        <div className="form-actions-footer">
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={isSaving}
            className="form-actions-footer__cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSaving || !canSubmit}
            disabledReason={getSubmitDisabledReason()}
            className="form-actions-footer__submit"
          >
            {isSaving ? <Spinner size="small" /> : null}
            {isSaving ? 'Creating...' : 'Create Client'}
          </Button>
        </div>
      </div>

      <PhotoCropModal
        isOpen={isCropOpen}
        imageSrc={cropImageSrc}
        fileName={cropFileMeta.fileName}
        mimeType={cropFileMeta.mimeType}
        onClose={closeCrop}
        onApply={handleCropApply}
        isApplying={isApplyingCrop || isUploadingMainPhoto}
      />
      <PhotoCropModal
        isOpen={isGalleryCropOpen}
        imageSrc={galleryCropImageSrc}
        fileName={galleryCropFileMeta.fileName}
        mimeType={galleryCropFileMeta.mimeType}
        onClose={closeGalleryCrop}
        onApply={handleGalleryCropApply}
        isApplying={isApplyingGalleryCrop}
      />
    </div>
  );
};
